// RepoChatWindow.js
// Chat-first Query UI component for repository Q&A

import React, { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, ChevronUp, Settings, MessageSquare, Plus, X, Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api, { QUERY_SEARCH_TIMEOUT_MS } from "./api";
import ErrorBoundary from "./ErrorBoundary";
import EngineeringWorkflowPanels from "./EngineeringWorkflowPanels";
import QueryAnswerEvidenceStrip from "./QueryAnswerEvidenceStrip";
import "./RepoChatWindow.css";
import "./ErrorBoundary.css";

/** Phase 2.2: Map weakContextReason code to human-readable banner text */
function weakContextReasonToLabel(code) {
  const map = {
    no_context_chunks: "No relevant analyzed chunks were retrieved",
    doc_only_retrieval: "Only documentation context was retrieved",
    fallback_only_retrieval: "Fallback context was used because semantic matches were weak",
    low_average_similarity: "Retrieved context had low semantic similarity",
    few_unique_files: "Context came from too few unique files",
    low_deterministic_coverage_tier: "Deterministic coverage tier is Low",
  };
  return map[code] || code;
}

function collectKnownLimitations(metadata) {
  const out = [];
  if (metadata?.weakContextReason) {
    out.push(weakContextReasonToLabel(metadata.weakContextReason));
  }
  if (Array.isArray(metadata?.answerSections?.knownLimitations)) {
    for (const lim of metadata.answerSections.knownLimitations) {
      if (typeof lim === "string" && lim.trim()) out.push(lim.trim());
    }
  }
  return [...new Set(out)];
}

const RepoChatWindow = ({ repoId, repoStatus, prefilledQuery, onNavigateToFeaturePlans }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState(new Set());
  const messagesEndRef = useRef(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [showConversationList, setShowConversationList] = useState(false);
  const [persistenceState, setPersistenceState] = useState(null); // 'saving', 'saved', 'error'
  const [conversationLoadError, setConversationLoadError] = useState(null);
  const activeLoadConversationIdRef = useRef(null); // Track which conversation we're currently loading

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load conversation history from backend
  const loadConversationHistory = async (repoId, conversationId, showError = true) => {
    if (!repoId || !conversationId) return;

    // Track that we're loading this conversation
    activeLoadConversationIdRef.current = conversationId;
    setLoadingHistory(true);
    setConversationLoadError(null);
    if (showError) {
      setError("");
    }

    try {
      const response = await api.get(`/queries/${repoId}/conversation/${conversationId}`);
      
      // Race condition check: verify this conversation is still the one we're loading
      if (activeLoadConversationIdRef.current !== conversationId) {
        console.log(`[RepoChatWindow] Ignoring stale conversation history for ${conversationId} (current load: ${activeLoadConversationIdRef.current})`);
        return; // Ignore stale response
      }

      // Conversation is still active, proceed with loading messages
      if (response.data && response.data.messages) {
        // Map backend messages to frontend format
        const loadedMessages = response.data.messages.map((msg) => {
          const message = {
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
          };

          // Include metadata if present (for assistant messages)
          if (msg.metadata) {
            message.metadata = {
              usedChunks: msg.metadata.usedChunks || [],
              usedFlows: msg.metadata.usedFlows || [],
              usedDocs: msg.metadata.usedDocs || [],
              relevantFiles: msg.metadata.relevantFiles || [],
              semanticSearch: msg.metadata.semanticSearchResults ? {
                resultsCount: msg.metadata.semanticSearchResults,
              } : undefined,
              flows: msg.metadata.usedFlows || [],
              confidence: msg.metadata.confidence,
              weakContextReason: msg.metadata.weakContextReason,
              confidenceFactors: msg.metadata.confidenceFactors,
              answerSections: msg.metadata.answerSections || null,
              coverageNote: msg.metadata.coverageNote || null,
            };
          }

          return message;
        });

        setMessages(loadedMessages);
        setConversationLoadError(null); // Clear any previous errors
      }
    } catch (err) {
      // Race condition check: verify this conversation is still the one we're loading
      if (activeLoadConversationIdRef.current !== conversationId) {
        console.log(`[RepoChatWindow] Ignoring stale error for conversation ${conversationId} (current load: ${activeLoadConversationIdRef.current})`);
        return; // Ignore stale error
      }

      // Conversation is still active, handle the error
      // 404 means conversation doesn't exist - clear it and start fresh
      if (err.response?.status === 404) {
        console.log(`[RepoChatWindow] Conversation ${conversationId} not found, starting new conversation`);
        setConversationId(null);
        localStorage.removeItem(`conversation-${repoId}`);
        setMessages([]);
        setConversationLoadError(null);
      } else {
        // Other errors - show error state with retry option
        const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || "Failed to load conversation history";
        console.error("[RepoChatWindow] Failed to load conversation history:", err);
        setConversationLoadError({
          message: errorMessage,
          conversationId,
          repoId,
        });
        if (showError) {
          setError("Failed to load conversation history. You can retry or start a new conversation.");
        }
      }
    } finally {
      // Only clear loading state if this conversation is still the active load
      if (activeLoadConversationIdRef.current === conversationId) {
        setLoadingHistory(false);
        activeLoadConversationIdRef.current = null;
      }
    }
  };

  // Retry loading conversation history
  const retryLoadConversation = async () => {
    if (conversationLoadError) {
      await loadConversationHistory(conversationLoadError.repoId, conversationLoadError.conversationId, true);
    }
  };

  // Load conversation list from backend
  const loadConversationList = async (repoId) => {
    if (!repoId) return;

    setLoadingConversations(true);
    try {
      const response = await api.get(`/queries/${repoId}/conversations`);
      if (response.data && response.data.conversations) {
        setConversations(response.data.conversations);
      }
    } catch (err) {
      // Non-blocking error - just log it
      console.error("[RepoChatWindow] Failed to load conversation list:", err);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Handle starting a new conversation
  const handleNewConversation = () => {
    // Clear the ref to prevent any in-flight loads from updating state
    activeLoadConversationIdRef.current = null;
    setConversationId(null);
    setMessages([]);
    setError("");
    setConversationLoadError(null);
    localStorage.removeItem(`conversation-${repoId}`);
    // New conversationId will be created by backend on first message
  };

  // Handle switching to a different conversation
  const handleSwitchConversation = async (newConversationId) => {
    if (newConversationId === conversationId) return; // Already active

    // Update the ref immediately to prevent stale responses from previous loads
    activeLoadConversationIdRef.current = newConversationId;
    setConversationId(newConversationId);
    localStorage.setItem(`conversation-${repoId}`, newConversationId);
    setMessages([]); // Clear current messages before loading new ones
    setConversationLoadError(null); // Clear any previous errors
    setError(""); // Clear any general errors
    await loadConversationHistory(repoId, newConversationId, true);
  };

  // Delete a conversation
  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      await api.delete(`/queries/${repoId}/conversation/${convId}`);
      setConversations((prev) => prev.filter((c) => c.conversationId !== convId));
      if (convId === conversationId) {
        setConversationId(null);
        setMessages([]);
        setError("");
        setConversationLoadError(null);
        localStorage.removeItem(`conversation-${repoId}`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.details || err.message || "Failed to delete conversation";
      setError(msg);
    }
  };

  // Initialize conversation on mount
  useEffect(() => {
    if (repoId && repoStatus === 'completed') {
      // Load conversation list
      loadConversationList(repoId);

      // Load conversationId from localStorage
      const storedId = localStorage.getItem(`conversation-${repoId}`);
      if (storedId) {
        setConversationId(storedId);
        // Load conversation history from backend
        loadConversationHistory(repoId, storedId);
      }
    }
  }, [repoId, repoStatus]);

  // Handle prefilled query
  useEffect(() => {
    if (prefilledQuery && prefilledQuery.trim() && !input) {
      setInput(prefilledQuery);
      // Focus the input field after setting the value
      const inputElement = document.querySelector('.chat-input');
      if (inputElement) {
        setTimeout(() => inputElement.focus(), 100);
      }
    }
  }, [prefilledQuery, input]);

  const handleSend = async () => {
    if (!input.trim() || !repoId || loading) return;

    const userMessage = input.trim();
    setInput("");
    setError("");

    // Add user message to UI immediately
    const newUserMessage = {
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newUserMessage]);

    setLoading(true);
    setPersistenceState('saving');

    try {
      // Wait long enough for RAG + LLM; short timeouts cause false errors while the server still saves the answer.
      const response = await api.post(
        `/queries/${repoId}/search`,
        {
          query: userMessage,
          conversationId: conversationId,
        },
        { timeout: QUERY_SEARCH_TIMEOUT_MS }
      );

      // Store conversationId if returned
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
        localStorage.setItem(`conversation-${repoId}`, response.data.conversationId);
        // Refresh conversation list to show updated timestamps
        loadConversationList(repoId);
      }

      // Store full response for debug/details
      setLastResponse(response.data);
      if (response.data.featureBuilderPreflight && repoId) {
        sessionStorage.setItem(
          `queryPreflight-${repoId}`,
          JSON.stringify(response.data.featureBuilderPreflight)
        );
      }

      // Add assistant message (Phase 4.1: use answerText when present, store answerSections)
      const content = response.data.answerText ?? response.data.answer;
      const assistantMessage = {
        role: "assistant",
        content,
        timestamp: new Date(),
        metadata: {
          usedChunks: response.data.usedChunks || [],
          usedFlows: response.data.usedFlows || [],
          usedDocs: response.data.usedDocs || [],
          relevantFiles: response.data.relevantFiles || [],
          semanticSearch: response.data.semanticSearch,
          flows: response.data.flows || [],
          confidence: response.data.confidence,
          weakContextReason: response.data.weakContextReason,
          confidenceFactors: response.data.confidenceFactors,
          answerSections: response.data.answerSections || null,
          coverageNote: response.data.coverageNote || null,
          engineeringWorkflowArtifacts: response.data.engineeringWorkflowArtifacts || null,
          artifactQualityValidation: response.data.artifactQualityValidation || null,
          artifactRepairAttempt: response.data.artifactRepairAttempt || null,
          featureBuilderPreflight: response.data.featureBuilderPreflight || null,
          cursorReadiness: response.data.cursorReadiness || null,
        },
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Show "Saved" indicator, then clear after 2 seconds
      setPersistenceState('saved');
      setTimeout(() => {
        setPersistenceState(null);
      }, 2000);
    } catch (err) {
      let errorMessage = "Failed to process query";
      if (err.code === "TIMEOUT" || err.message?.includes?.("timeout")) {
        errorMessage =
          "The answer is taking longer than the browser waited. The server may still be working—the reply is often saved. Wait a bit, then refresh the page or pick this conversation again from the list.";
      } else if (err.response) {
        errorMessage = err.response.data?.error || err.response.data?.details || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "Network error: Unable to connect to server.";
      } else {
        errorMessage = err.message || "An unexpected error occurred";
      }
      setError(errorMessage);
      setPersistenceState('error');
      // Clear error state after 5 seconds
      setTimeout(() => {
        setPersistenceState(null);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetails = (messageIndex) => {
    setExpandedDetails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageIndex)) {
        newSet.delete(messageIndex);
      } else {
        newSet.add(messageIndex);
      }
      return newSet;
    });
  };

  if (repoStatus !== 'completed') {
    return (
      <div className="chat-window">
        <div className="chat-not-ready">
          <p><strong>Repository not ready for queries</strong></p>
          <p>
            {repoStatus === 'pending' && 'Repository is queued for processing.'}
            {repoStatus === 'processing' && 'Repository is currently being analyzed.'}
            {repoStatus === 'failed' && 'Repository analysis failed. Please re-submit.'}
            {!['pending', 'processing', 'failed'].includes(repoStatus) && 'Please wait for repository analysis to complete.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      showDetails={process.env.NODE_ENV === 'development'}
      onReset={() => {
        setMessages([]);
        setInput("");
        setError("");
        setConversationId(null);
        setConversationLoadError(null);
      }}
    >
      <div className="chat-window">
      {/* Conversation list sidebar */}
      {showConversationList && (
        <div className="conversation-sidebar">
          <div className="conversation-sidebar-header">
            <h3>Conversations</h3>
            <button
              onClick={() => setShowConversationList(false)}
              className="conversation-sidebar-close"
              aria-label="Close conversation list"
            >
              <X size={20} />
            </button>
          </div>
          <button
            onClick={handleNewConversation}
            className="new-conversation-btn"
          >
            <Plus size={16} />
            New Conversation
          </button>
          <div className="conversation-list">
            {loadingConversations ? (
              <div className="conversation-list-loading">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="conversation-list-empty">
                <p>No conversations yet</p>
                <p className="conversation-list-empty-hint">Start a conversation to see it here</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.conversationId}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSwitchConversation(conv.conversationId)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSwitchConversation(conv.conversationId); } }}
                  className={`conversation-item ${conv.conversationId === conversationId ? 'active' : ''}`}
                >
                  <div className="conversation-item-content">
                    <div className="conversation-item-preview">
                      {conv.firstMessagePreview || 'Empty conversation'}
                    </div>
                    <div className="conversation-item-meta">
                      <span className="conversation-item-count">{conv.messageCount} messages</span>
                      <span className="conversation-item-date">
                        {new Date(conv.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="conversation-item-delete"
                    onClick={(e) => handleDeleteConversation(conv.conversationId, e)}
                    title="Delete conversation"
                    aria-label="Delete conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main chat area - wrapped in container */}
      <div className="chat-content">
        <div className="chat-main">
          {/* Conversation list toggle button */}
          <div className="chat-header">
            <button
              onClick={() => setShowConversationList(!showConversationList)}
              className="conversation-list-toggle"
              aria-label="Toggle conversation list"
            >
              <MessageSquare size={20} />
            </button>
            {!showConversationList && (
              <button
                onClick={handleNewConversation}
                className="new-conversation-header-btn"
              >
                <Plus size={16} />
                New Conversation
              </button>
            )}
            <div className="chat-header-spacer" />
            {persistenceState && (
              <div className={`persistence-indicator ${persistenceState}`}>
                {persistenceState === 'saving' && (
                  <>
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span>Saving...</span>
                  </>
                )}
                {persistenceState === 'saved' && (
                  <span>✓ Saved</span>
                )}
                {persistenceState === 'error' && (
                  <span>✗ Error saving</span>
                )}
              </div>
            )}
          </div>
          <div className="chat-messages">
          {loadingHistory && (
            <div className="chat-message assistant">
              <div className="message-bubble assistant-bubble">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p style={{ marginTop: '8px', fontSize: '0.9em', opacity: 0.7 }}>Loading conversation history...</p>
              </div>
            </div>
          )}
          {conversationLoadError && !loadingHistory && (
            <div className="conversation-load-error">
              <div className="conversation-load-error-content">
                <p><strong>Failed to load conversation</strong></p>
                <p className="conversation-load-error-message">{conversationLoadError.message}</p>
                <div className="conversation-load-error-actions">
                  <button
                    onClick={retryLoadConversation}
                    className="conversation-load-error-retry"
                  >
                    Retry
                  </button>
                  <button
                    onClick={handleNewConversation}
                    className="conversation-load-error-new"
                  >
                    Start New Conversation
                  </button>
                </div>
              </div>
            </div>
          )}
          {!loadingHistory && !conversationLoadError && messages.length === 0 && (
            <div className="chat-welcome">
              <h3>Ask questions about this repository</h3>
              <p>I can help you understand the codebase, find specific functionality, and explain how things work.</p>
              <p className="chat-helper-text">Unlimited questions during pre-launch. Credits are not deducted for queries.</p>
            </div>
          )}

          {messages.map((message, index) => (
            <div key={index} className={`chat-message ${message.role}`}>
              <div className="message-content">
                {message.role === "user" ? (
                  <div className="message-bubble user-bubble">
                    {message.content}
                  </div>
                ) : (
                  <>
                    {(message.metadata?.coverageNote || collectKnownLimitations(message.metadata).length > 0) && (
                      <div className="query-transparency-inline" role="status">
                        {message.metadata?.coverageNote && (
                          <div className="query-transparency-row">
                            <strong>Coverage:</strong> {message.metadata.coverageNote}
                          </div>
                        )}
                        {collectKnownLimitations(message.metadata).length > 0 && (
                          <div className="query-transparency-row">
                            <strong>Limitations:</strong> {collectKnownLimitations(message.metadata).join(" | ")}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="message-bubble assistant-bubble">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                    <QueryAnswerEvidenceStrip metadata={message.metadata} />
                    {message.metadata?.engineeringWorkflowArtifacts && (
                      <EngineeringWorkflowPanels
                        variant="query_repository"
                        engineeringWorkflowArtifacts={message.metadata.engineeringWorkflowArtifacts}
                        artifactQualityValidation={message.metadata.artifactQualityValidation}
                        artifactRepairAttempt={message.metadata.artifactRepairAttempt}
                        cursorReadiness={message.metadata.cursorReadiness}
                        onCreateFeaturePlan={
                          typeof onNavigateToFeaturePlans === "function"
                            ? () => onNavigateToFeaturePlans(message)
                            : undefined
                        }
                      />
                    )}
                    {message.metadata?.confidence &&
                      !message.metadata?.answerSections &&
                      !message.metadata?.relevantFiles?.length && (
                      <div className="query-confidence-badge-wrap">
                        <span className={`query-confidence-badge query-confidence-${message.metadata.confidence.toLowerCase()}`}>
                          Confidence: {message.metadata.confidence}
                        </span>
                        {collectKnownLimitations(message.metadata).length > 0 && (
                          <span className="query-confidence-caveat">
                            Caveat: limited/partial evidence
                          </span>
                        )}
                      </div>
                    )}
                    {message.metadata && (
                      <div className="message-details-toggle">
                        <button
                          onClick={() => toggleDetails(index)}
                          className="details-toggle-btn"
                        >
                          {expandedDetails.has(index) ? (
                            <>
                              <ChevronUp size={16} />
                              Hide analysis details
                            </>
                          ) : (
                            <>
                              <ChevronDown size={16} />
                              Show analysis details
                            </>
                          )}
                        </button>
                        {expandedDetails.has(index) && (
                          <div className="message-details">
                            {message.metadata.answerSections ? (
                              <>
                                {message.metadata.answerSections.knownLimitations && message.metadata.answerSections.knownLimitations.length > 0 && (
                                  <div className="detail-section">
                                    <h4>Known Limitations</h4>
                                    <ul>
                                      {message.metadata.answerSections.knownLimitations.map((lim, i) => (
                                        <li key={i}>{lim}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {message.metadata.answerSections.evidenceSummary && (
                                  <div className="detail-section">
                                    <h4>Evidence Summary</h4>
                                    {message.metadata.answerSections.evidenceSummary.relevantFiles?.length > 0 && (
                                      <>
                                        <h5 className="detail-subhead">Relevant Files</h5>
                                        <ul>
                                          {message.metadata.answerSections.evidenceSummary.relevantFiles.map((f, i) => (
                                            <li key={i}>{f.filePath}{f.reason ? ` — ${f.reason}` : ""}</li>
                                          ))}
                                        </ul>
                                      </>
                                    )}
                                    {message.metadata.answerSections.evidenceSummary.evidenceItems?.length > 0 && (
                                      <>
                                        <h5 className="detail-subhead">Evidence Items</h5>
                                        <ul>
                                          {message.metadata.answerSections.evidenceSummary.evidenceItems.map((item, i) => (
                                            <li key={i}>
                                              <code className="evidence-file">{item.filePath}</code>
                                              {item.startLine != null && ` L${item.startLine}${item.endLine != null ? `–${item.endLine}` : ""}`}
                                              {item.note && ` — ${item.note}`}
                                            </li>
                                          ))}
                                        </ul>
                                      </>
                                    )}
                                    {(!message.metadata.answerSections.evidenceSummary.relevantFiles?.length && !message.metadata.answerSections.evidenceSummary.evidenceItems?.length) && (
                                      <p className="detail-empty">No evidence items.</p>
                                    )}
                                  </div>
                                )}
                                {message.metadata.answerSections.whatToCheckNext && message.metadata.answerSections.whatToCheckNext.length > 0 && (
                                  <div className="detail-section">
                                    <h4>What To Check Next</h4>
                                    <ul>
                                      {message.metadata.answerSections.whatToCheckNext.map((s, i) => (
                                        <li key={i}>{s}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </>
                            ) : null}
                            {message.metadata.relevantFiles && message.metadata.relevantFiles.length > 0 && (
                              <div className="detail-section">
                                <h4>Key Files ({message.metadata.relevantFiles.length})</h4>
                                <ul>
                                  {message.metadata.relevantFiles.slice(0, 5).map((file, i) => (
                                    <li key={i}>{file}</li>
                                  ))}
                                  {message.metadata.relevantFiles.length > 5 && (
                                    <li className="detail-more">+{message.metadata.relevantFiles.length - 5} more</li>
                                  )}
                                </ul>
                              </div>
                            )}
                            {message.metadata.semanticSearch && message.metadata.semanticSearch.topResults && message.metadata.semanticSearch.topResults.length > 0 && (
                              <div className="detail-section">
                                <h4>Semantic Search Results ({message.metadata.semanticSearch.resultsCount})</h4>
                                <ul>
                                  {message.metadata.semanticSearch.topResults.slice(0, 3).map((result, i) => (
                                    <li key={i}>
                                      {result.file_path || result.doc_path}
                                      {result.unit_name && ` (${result.unit_name})`}
                                      {result.similarity && ` - ${(result.similarity * 100).toFixed(1)}% match`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {message.metadata.flows && message.metadata.flows.length > 0 && (
                              <div className="detail-section">
                                <h4>Related Flows ({message.metadata.flows.length})</h4>
                                <ul>
                                  {message.metadata.flows.map((flow, i) => (
                                    <li key={i}>{flow.name}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="chat-message assistant">
              <div className="message-bubble assistant-bubble">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="chat-error">
              <p>{error}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-input-container">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && !loading && handleSend()}
            placeholder="Ask about the codebase..."
            className="chat-input"
            disabled={loading}
          />
          <button
            onClick={handleSend}
            className="chat-send-btn"
            disabled={loading || !input.trim()}
          >
            <Search size={20} />
          </button>
        </div>

        {lastResponse && (
          <div className="chat-debug-toggle">
            <button
              onClick={() => setShowDebug(!showDebug)}
              className="debug-toggle-btn"
            >
              <Settings size={16} />
              {showDebug ? "Hide" : "Show"} Debug Info
            </button>
            {showDebug && (
              <div className="chat-debug">
                <h4>Debug Information</h4>
                <pre>{JSON.stringify(lastResponse, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </ErrorBoundary>
  );
};

export default RepoChatWindow;

